import { fabric } from 'fabric';
import { cloneDeep } from 'lodash';

import { DeviceSearchRequest } from 'api/DeviceRequest';
import Equipment from 'model/Equipment';
import { LockingInfo } from 'model/LockingInfo';
import { LotoBook } from 'model/LotoBook';
import { DEVICE_MODAL_TYPE, VCM_LOCK_TYPE, YN_TYPE } from 'types/enumType';

export const MODAL_SIZE = 1800;
export const MIN_SCALE = 0.2; // 임의 설정, 너무 작지 않게
export const SIDE_BAR = 200;

export const isAdminPage = (path: string) =>
  path.startsWith('/admin') ||
  path.startsWith('/install') ||
  path.startsWith('/category') ||
  path.startsWith('/lot') ||
  path.startsWith('/warehouse') ||
  path.startsWith('/user-log') ||
  path.startsWith('/setting');

export const convertStringToNumber = (value: string) => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? 0 : num;
};

export const convertYnToBoolean = (value: YN_TYPE): boolean =>
  value === YN_TYPE.Y;
export const convertBooleanToYn = (value: boolean): YN_TYPE =>
  value ? YN_TYPE.Y : YN_TYPE.N;

export const convertDeviceTypeToString = (deviceType: DEVICE_MODAL_TYPE) => {
  switch (deviceType) {
    case DEVICE_MODAL_TYPE.PART:
      return '공장';
    case DEVICE_MODAL_TYPE.AREA:
      return 'Area';
    case DEVICE_MODAL_TYPE.SECTION:
      return 'Zone';
    default:
      return '설비';
  }
};


export const isLockedByVcmLock = (vcmLockInfo: VCM_LOCK_TYPE) => {
  if (vcmLockInfo === null) {
    return '';
  }

  switch (vcmLockInfo) {
    case VCM_LOCK_TYPE.LOCK:
      return 'lock';
    case VCM_LOCK_TYPE.UNLOCK:
      return 'unlock';
    case VCM_LOCK_TYPE.SET:
      return 'set';
    default:
      return '';
  }
};

export const isLockedByLockingInfo = (lockingInfo: LockingInfo) => {
  if (!lockingInfo || !(lockingInfo.lockRegDate || lockingInfo.unlockRegDate)) {
    return '';
  }

  const { lockRegDate, unlockRegDate } = lockingInfo;
  return lockRegDate && !unlockRegDate ? 'lock' : 'unlock';
};

export const isLockedByLockingInfoList = (lockingInfoList: LockingInfo[]) => {
  if (!lockingInfoList || lockingInfoList.length === 0) {
    return false;
  }

  return lockingInfoList.some(lockingInfo =>
    isLockedByLockingInfo(lockingInfo) === 'lock' || !isLockedByLockingInfo(lockingInfo),
  );
};

export const drawLockingInfo = (info: LockingInfo) => {
  if (!info) {
    return '';
  }
  let lockedInfoString = `${info.lockUserName} / ${info.lockRegDate}`;
  lockedInfoString += !info.unlockRegDate
    ? ''
    : `\n\r${info.unlockUserName} / ${info.unlockRegDate}`;

  return lockedInfoString;
};

const generateLotoActionNoByLockInfo = (
  lockInfo: LockingInfo[],
  startIndex: number,
): number => {
  let acc = startIndex;
  if (lockInfo !== null && lockInfo?.length > 0) {
    lockInfo?.forEach(item => {
      item.no = acc;
      acc += 1;
    });
  }
  return acc;
};

export const generateLogInfoNo = (logInfo: LotoBook): LotoBook => {
  let index = 1;
  const cloneLogInfo = cloneDeep(logInfo);
  index = generateLotoActionNoByLockInfo(cloneLogInfo.prodLocks, index);
  index = generateLotoActionNoByLockInfo(cloneLogInfo.elecLocks, index);
  index = generateLotoActionNoByLockInfo(cloneLogInfo.mechLocks, index);
  index = generateLotoActionNoByLockInfo(cloneLogInfo.etcLocks, index);

  return cloneLogInfo;
};

export const getDeviceIdByType = (searchRequest: DeviceSearchRequest, type: DEVICE_MODAL_TYPE) => {
  switch (type) {
    case DEVICE_MODAL_TYPE.PART:
      return searchRequest.partId!;
    case DEVICE_MODAL_TYPE.AREA:
      return searchRequest.zoneId!;
    case DEVICE_MODAL_TYPE.SECTION:
      return searchRequest.sectionId!;
    default:
      return searchRequest.equipId!;
  }
};
export const getStrokeColor = (type: DEVICE_MODAL_TYPE) => {
  switch (type) {
    case DEVICE_MODAL_TYPE.PART:
      return '#c8294b';
    case DEVICE_MODAL_TYPE.AREA:
      return '#3461eb';
    case DEVICE_MODAL_TYPE.SECTION:
      return '#4bb505';
    default:
      return '#000';
  }
}

export const getStrokeColorInStatusBoard = (type: DEVICE_MODAL_TYPE) => {
  switch (type) {
    case DEVICE_MODAL_TYPE.PART:
      return '#000000';
    case DEVICE_MODAL_TYPE.AREA:
      return '#000000';
    case DEVICE_MODAL_TYPE.SECTION:
      return '#6a8fff';
    default:
      return '#000';
  }
}

/**
 * 
 * @param imageRatio 이미지 비율(height/width)
 * @param imageWidth 이미지 width
 * @param windowWidth 윈도우 width
 * @returns scale
 * @description 이미지 비율과 윈도우 width를 이용하여 scale을 계산합니다.
 */
export const getScaleAndRatio = (canvasImage: fabric.Image, windowSize: { width: number, height: number }): { imgRatio: number, scale: number } => {
  const { width, height } = canvasImage;
  if (!width || !height) {
    return {
      imgRatio: 1,
      scale: 1
    }
  }

  const imgRatio = Math.min(height / width, width / height);
  const windowView = width > height ? (windowSize.width - 200) / width : (windowSize.height - 200) / height;
  const scale = Math.max(imgRatio * windowView, MIN_SCALE);

  return {
    imgRatio,
    scale
  }
}


export const createNewRectByEquipment = (info: Equipment, type: DEVICE_MODAL_TYPE) =>
  new fabric.Rect({
    left: 0,
    top: 0,
    width: 100,
    height: 100,
    fill: 'rgba(255, 255, 255, 0.0)',
    stroke: getStrokeColor(type),
    strokeWidth: 2,
    selectable: true,
    name: `${type}/${info.id}`
  });

export const createRectByEquipListAndRatio = (equipList: Equipment[], type: DEVICE_MODAL_TYPE, ratio: number, selectable: boolean, fill: string = 'rgba(255, 255, 255, 0.0)') =>
  equipList.filter(item => item.width > 0)
    .map(item =>
      new fabric.Rect({
        left: Math.round(item.leftPos * ratio),
        top: Math.round(item.topPos * ratio),
        width: Math.round(item.width * ratio),
        height: Math.round(item.height * ratio),
        fill,
        stroke: getStrokeColor(type),
        strokeWidth: 2,
        selectable,
        name: `${type}/${item.id}`,
      })
    );

export const createRectByEquipmentAndRatio = (info: Equipment, type: DEVICE_MODAL_TYPE, ratio: number, selectable: boolean, fill: string = 'rgba(255, 255, 255, 0.0)') =>
  new fabric.Rect({
    left: Math.round(info.leftPos * ratio),
    top: Math.round(info.topPos * ratio),
    width: Math.round(info.width * ratio),
    height: Math.round(info.height * ratio),
    fill,
    stroke: getStrokeColor(type),
    strokeWidth: 2,
    selectable,
    name: `${type}/${info.id}`,
  });


export const createRectByEquipmentAndRatioInStatusBoard = (info: Equipment, type: DEVICE_MODAL_TYPE, ratio: number, selectable: boolean, fill: string = 'rgba(255, 255, 255, 0.0)') =>
  new fabric.Rect({
    left: info.leftPos * ratio,
    top: info.topPos * ratio,
    width: info.width * ratio,
    height: info.height * ratio,
    fill,
    stroke: getStrokeColorInStatusBoard(type),
    strokeWidth: 0.5,
    selectable,
    name: `${type}/${info.id}`,
  });



export const filterRectByType = (objects: fabric.Object[], type: DEVICE_MODAL_TYPE) => objects.filter(obj => obj.name?.includes(type));

export const generatePopupContent = (content: string): string[] => [
  '<html><head><title></title>',
  `
    <link rel="stylesheet" href="/assets/css/common.css">
    <link rel="stylesheet" href="/assets/css/style.css">
    <link rel="stylesheet" href="/assets/css/layout.css">
    <link rel="stylesheet" href="/assets/css/content.css">
    <link rel="stylesheet" href="/assets/css/fontawesome.min.css">`,
  '</head><body>',
  content,
  '</body></html>',
]


const sqlRegex = /select|from|and|or|union|declare|cast|to|char|having|where|xp_cmdshell|update|into|insert/gmi;
const specialCharRegx = /[{}\[\]\/?.,;:|)*~!^+<>#$%&\=(’"]/gmi;
export const containsSpecialCharAndSql = (msg: string) => sqlRegex.test(msg) || specialCharRegx.test(msg);

export const generateShiftWork = (work1: string, work2: string, work3: string, work4: string): string =>
  `${work1}->${work2}->${work3}->${work4}`;

export const get24Hour = () => {
  const hourList = [];
  for (let i = 0; i < 24; i += 1) {
    hourList.push(i);
  }

  return hourList;
}

export const shiftGroupList: string[] = ['A', 'B', 'C', 'D'];


export const removeEmptyParams = (params: any) => {
  const result = { ...params };
  Object.keys(result).forEach(key => {
    const keyVal = result[key];
    if (keyVal === null || keyVal === undefined || keyVal === '') {
      delete result[key];
    }
  })

  return result;
}